<script setup lang="ts">
import type { Account } from '../types'
import ScrollContainer from '@/components/base/_internal/scroll-container/'
import { computed } from 'vue'
import { useI18n } from '@/composables/i18n'

const { i18n } = useI18n()

defineOptions({
    compatConfig: {
        MODE: 3,
    },
})

const props = defineProps<{
    accounts: Account[]
}>()

const sortedAccounts = computed(() => {
    return props.accounts.slice().sort((a, b) => {
        return (a.institution ?? '').localeCompare(b.institution ?? '')
            || (a.fullname ?? '').localeCompare(b.fullname ?? '')
            || (a.role ?? '').localeCompare(b.role ?? '')
    })
})

const groupedAccounts = computed(() => {
    const groups: Record<string, Account[]> = {}
    for (const account of sortedAccounts.value) {
        const institution = account.institution ?? ''
        if (!groups[institution]) {
            groups[institution] = []
        }
        groups[institution].push(account)
    }
    return groups
})

</script>

<template>
    <div class="account-selection">
        <header>
            <h2>{{ i18n.t('Konto auswählen') }}</h2>
            <p>
                {{ i18n.t('Sie haben mehrere Accounts mit derselben E-Mail-Adresse angelegt. Bitte wählen Sie einen der folgenden Accounts.') }}
            </p>
        </header>

        <ScrollContainer class="accounts">
            <ul class="account-groups">
                <li
                    v-for="(groupAccounts, institution) in groupedAccounts"
                    :key="institution"
                >
                    <h3
                        v-if="institution"
                        class="group-name"
                    >
                        {{ institution }}
                    </h3>

                    <ul class="account-list">
                        <li
                            v-for="account in groupAccounts"
                            :key="account.url"
                        >
                            <a
                                :href="account.url"
                                class="account-item"
                            >
                                <span class="account-label">{{ account.fullname }}</span>
                                <span class="account-role">{{ account.role }}</span>
                            </a>
                        </li>
                    </ul>
                </li>
            </ul>
        </ScrollContainer>
    </div>
</template>

<style scoped>
@reference "@/assets/styles/config.css";

.account-selection {
    @apply flex flex-col gap-4;
    flex: 1;
    min-height: 0;
}

header {
    h2 {
        @apply text-xl font-black mb-2;
    }

    p {
        @apply text-gray-500 leading-normal font-medium;
    }
}

:deep(.accounts) {
    flex: 1;
    min-height: 0;
    width:calc(100% + 2 * var(--overlay-px));
    margin-left:calc(-1 * var(--overlay-px));
    margin-bottom:calc(-1 * var(--overlay-px));
    padding:0;
    --inner-px: var(--overlay-px);
    --inner-scrollbar-margin: 0px;
}

.account-groups {
    @apply flex flex-col py-1 gap-4;
    flex: 1;
    min-height: 0;
    margin-bottom:var(--overlay-py);
}

.group-name {
    @apply text-lg font-bold text-gray-600 px-4 py-2;
}

.account-list {
    @apply flex flex-col gap-2;
}

.account-item {
    @apply flex flex-row justify-between gap-0.5 rounded-lg border px-4 py-3 font-semibold transition-colors;
    border-color:#dde6ec;
    color:#415F70;

    &:hover {
        @apply border-gray-400 bg-gray-50;
    }
}

.account-role {
    @apply text-sm font-medium text-gray-500;
}
</style>
